/**
 * Local Storage Service
 * Provides a unified interface for storing and retrieving data locally
 * Simulates a backend database using browser localStorage and IndexedDB
 */

class LocalStorageService {
  constructor() {
    this.storagePrefix = 'medfast_';
    this.init();
  }

  init() {
    // Initialize storage keys if they don't exist
    const requiredKeys = [
      'facilities',
      'wards',
      'ndis_participants',
      'ndis_budgets',
      'ndis_relationships',
      'ndis_nominees',
      'ndis_restrictive_practices',
      'agedcare_residents',
      'agedcare_anacc_assessments',
      'agedcare_enrmc_orders',
      'agedcare_s8_register',
      'agedcare_s8_summary',
      'agedcare_adverse_drug_reactions',
      'agedcare_support_at_home',
      'proda_devices',
      'audit_logs',
      'consent_dashboard',
      'shadow_ledgers',
      'support_catalogue',
      'service_deliveries',
      'incident_reports',
      'medfast_complaints',
      'scheduled_reports',
      'generated_reports',
      'rooms',
      'dashboard_configs',
      'custom_reports'
    ];

    requiredKeys.forEach(key => {
      if (!this.get(key)) {
        this.set(key, []);
      }
    });
  }

  /**
   * Get data from localStorage
   * @param {string} key - Storage key (without prefix)
   * @param {*} defaultValue - Default value if key doesn't exist
   * @returns {*} Stored data or default value
   */
  get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(this.storagePrefix + key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error reading from localStorage for key ${key}:`, error);
      return defaultValue;
    }
  }

  /**
   * Set data in localStorage
   * @param {string} key - Storage key (without prefix)
   * @param {*} value - Data to store
   * @returns {boolean} Success status
   */
  set(key, value) {
    try {
      localStorage.setItem(this.storagePrefix + key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Error writing to localStorage for key ${key}:`, error);
      return false;
    }
  }

  /**
   * Remove data from localStorage
   * @param {string} key - Storage key (without prefix)
   * @returns {boolean} Success status
   */
  remove(key) {
    try {
      localStorage.removeItem(this.storagePrefix + key);
      return true;
    } catch (error) {
      console.error(`Error removing from localStorage for key ${key}:`, error);
      return false;
    }
  }

  /**
   * Get all items with a specific prefix
   * @param {string} prefix - Key prefix to search for
   * @returns {Object} Object with all matching keys and values
   */
  getAll(prefix = '') {
    const items = {};
    const searchPrefix = this.storagePrefix + prefix;
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(searchPrefix)) {
        const shortKey = key.replace(this.storagePrefix, '');
        items[shortKey] = this.get(shortKey);
      }
    }
    
    return items;
  }

  /**
   * Clear all application data
   * @returns {boolean} Success status
   */
  clearAll() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.storagePrefix)) {
          localStorage.removeItem(key);
        }
      });
      this.init();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }

  /**
   * Add item to array in storage
   * @param {string} key - Storage key
   * @param {*} item - Item to add
   * @returns {*} Added item with generated ID
   */
  addItem(key, item) {
    const items = this.get(key, []);
    
    // Check if item with same ID already exists
    if (item.id) {
      const existingIndex = items.findIndex(existing => existing.id === item.id);
      if (existingIndex !== -1) {
        // Update existing item instead of creating duplicate
        const previousValue = JSON.parse(JSON.stringify(items[existingIndex]));
        items[existingIndex] = {
          ...items[existingIndex],
          ...item,
          updatedAt: new Date().toISOString()
        };
        
        // Log audit trail
        this.logAudit({
          action_type: 'UPDATE',
          record_type: key,
          record_id: item.id,
          previous_value: previousValue,
          new_value: items[existingIndex],
          user_id: item.createdBy || null,
          success: true
        });
        
        this.set(key, items);
        return items[existingIndex];
      }
    }
    
    const newItem = {
      ...item,
      id: item.id || this.generateId(),
      createdAt: item.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    items.push(newItem);
    
    // Log audit trail
    this.logAudit({
      action_type: 'CREATE',
      record_type: key,
      record_id: newItem.id,
      new_value: newItem,
      user_id: item.createdBy || null,
      success: true
    });
    
    this.set(key, items);
    return newItem;
  }

  /**
   * Update item in array
   * @param {string} key - Storage key
   * @param {string} id - Item ID
   * @param {Object} updates - Fields to update
   * @returns {*} Updated item or null
   */
  updateItem(key, id, updates) {
    const items = this.get(key, []);
    const index = items.findIndex(item => item.id === id);
    
    if (index === -1) {
      return null;
    }

    items[index] = {
      ...items[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    this.set(key, items);
    return items[index];
  }

  /**
   * Remove item from array
   * @param {string} key - Storage key
   * @param {string} id - Item ID
   * @returns {boolean} Success status
   */
  removeItem(key, id) {
    const items = this.get(key, []);
    const itemToDelete = items.find(item => item.id === id);
    const filtered = items.filter(item => item.id !== id);
    
    // Log audit trail before deletion
    // Note: Services should handle audit logging with userId when calling removeItem
    // This is a low-level method; services pass userId in their own audit logs
    if (itemToDelete) {
      this.logAudit({
        action_type: 'DELETE',
        record_type: key,
        record_id: id,
        previous_value: itemToDelete,
        new_value: null,
        user_id: null, // Services handle audit logging with userId
        success: true
      });
    }
    
    this.set(key, filtered);
    return filtered.length < items.length;
  }

  /**
   * Find item by ID
   * @param {string} key - Storage key
   * @param {string} id - Item ID
   * @returns {*} Item or null
   */
  findById(key, id) {
    const items = this.get(key, []);
    return items.find(item => item.id === id) || null;
  }

  /**
   * Find items by criteria
   * @param {string} key - Storage key
   * @param {Function} predicate - Filter function
   * @returns {Array} Matching items
   */
  find(key, predicate) {
    const items = this.get(key, []);
    return items.filter(predicate);
  }

  /**
   * Generate unique ID
   * @returns {string} Unique ID
   */
  generateId() {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log audit event (Immutable - cannot be modified)
   * Based on NDIS Practice Standards - Complete audit trail requirements
   * @param {Object} event - Audit event data
   */
  logAudit(event) {
    const logs = this.get('audit_logs', []);
    
    // Create immutable audit record with all required fields
    const auditRecord = {
      event_id: event.event_id || this.generateId(),
      timestamp: event.timestamp || new Date().toISOString(), // ISO 8601 with timezone
      user_id: event.user_id || null,
      user_role: event.user_role || null,
      action_type: event.action_type || 'UNKNOWN', // CREATE|READ|UPDATE|DELETE|LOGIN|LOGOUT|EXPORT
      record_type: event.record_type || null,
      record_id: event.record_id || null,
      previous_value: event.previous_value || null, // For updates
      new_value: event.new_value || null, // For updates and creates
      ip_address: event.ip_address || null,
      success: event.success !== undefined ? event.success : true,
      metadata: event.metadata || {},
      // Immutable flag - prevents modification
      immutable: true,
      created_at: new Date().toISOString()
    };
    
    logs.push(auditRecord);
    
    // Keep audit logs for 7 years minimum (approximately 2.2 million records)
    // For now, keep last 100,000 logs (should be increased in production)
    if (logs.length > 100000) {
      // Remove oldest logs, but keep at least 7 years worth
      const sevenYearsAgo = new Date();
      sevenYearsAgo.setFullYear(sevenYearsAgo.getFullYear() - 7);
      
      const filtered = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= sevenYearsAgo;
      });
      
      // If still too many, remove oldest
      if (filtered.length > 100000) {
        filtered.splice(0, filtered.length - 100000);
      }
      
      this.set('audit_logs', filtered);
    } else {
      this.set('audit_logs', logs);
    }
    
    return auditRecord;
  }

  /**
   * Get audit trail for a specific record
   * @param {string} recordType - Type of record
   * @param {string} recordId - Record ID
   * @returns {Array} Audit trail entries
   */
  getAuditTrail(recordType, recordId) {
    const logs = this.get('audit_logs', []);
    return logs.filter(log => 
      log.record_type === recordType && log.record_id === recordId
    ).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }

  /**
   * Get audit trail for a user
   * @param {string} userId - User ID
   * @returns {Array} Audit trail entries
   */
  getUserAuditTrail(userId) {
    const logs = this.get('audit_logs', []);
    return logs.filter(log => log.user_id === userId)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }
}

// Export singleton instance
export default new LocalStorageService();

